import { FC, ReactNode } from "react"
import {
  Briefcase,
  User,
  Link2,
  Share2,
  MapPin,
  ExternalLink,
  Facebook,
  Linkedin,
  Instagram,
  Youtube,
  Calendar,
  Globe
} from "lucide-react"
import { LoanOfficer } from "../../../../types"
import GoogleReviewsList from "../../google-reviews-list"
import If from "../../if"
import { useFetchBrandReviewsQuery } from "../../../../redux/queries"

interface Props {
  loanOfficer: LoanOfficer
}

interface SectionCardProps {
  title: string
  icon: ReactNode
  children: ReactNode
  delay: number
}

const SectionCard: FC<SectionCardProps> = ({
  title,
  icon,
  children,
  delay
}) => (
  <div
    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-fade-slide-in opacity-0"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center gap-2 mb-4">
      <span className="text-primary-500">{icon}</span>
      <h2 className="font-serif text-lg font-semibold text-slate-800">
        {title}
      </h2>
    </div>
    {children}
  </div>
)

interface FieldProps {
  label: string
  value: string | null | undefined
}

const Field: FC<FieldProps> = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">
      {label}
    </dt>
    <dd className="mt-1 text-slate-700">
      {value || <span className="text-slate-300 italic">Not provided</span>}
    </dd>
  </div>
)

interface LinkRowProps {
  label: string
  href: string | null | undefined
}

const LinkRow: FC<LinkRowProps> = ({ label, href }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
    <span className="text-sm text-slate-600">{label}</span>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
      >
        <span className="max-w-xs truncate">{href}</span>
        <ExternalLink size={14} />
      </a>
    ) : (
      <span className="text-sm text-slate-300 italic">Not provided</span>
    )}
  </div>
)

interface SocialLinkProps {
  icon: ReactNode
  href: string
  label: string
}

const SocialLink: FC<SocialLinkProps> = ({ icon, href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 hover:bg-primary-50 hover:text-primary-600 transition-colors"
  >
    {icon}
    <span className="text-sm">{label}</span>
  </a>
)

const LoanOfficerOverview: FC<Props> = ({ loanOfficer }) => {
  const { data: reviews } = useFetchBrandReviewsQuery(
    {
      place_id: loanOfficer?.google_place_id ?? ""
    },
    {
      skip: !loanOfficer || !loanOfficer?.google_place_id
    }
  )

  const socialLinks = [
    {
      icon: <Facebook size={18} />,
      href: loanOfficer.facebook_link,
      label: "Facebook"
    },
    {
      icon: <Linkedin size={18} />,
      href: loanOfficer.linkedin_link,
      label: "LinkedIn"
    },
    {
      icon: <Calendar size={18} />,
      href: loanOfficer.calendly_link,
      label: "Calendly"
    },
    {
      icon: <Instagram size={18} />,
      href: loanOfficer.instagram_link,
      label: "Instagram"
    },
    {
      icon: <Youtube size={18} />,
      href: loanOfficer.youtube_link,
      label: "YouTube"
    },
    {
      icon: <Globe size={18} />,
      href: loanOfficer.website_link,
      label: "Website"
    }
  ].filter(link => link.href)

  return (
    <div className="space-y-6">
      <SectionCard
        title="Professional Info"
        icon={<Briefcase size={20} />}
        delay={0}
      >
        <dl className="grid grid-cols-2 gap-6">
          <Field label="Title" value={loanOfficer.position} />
          <Field label="NMLS" value={loanOfficer.nmls} />
          <Field label="Mobile" value={loanOfficer.mobile_phone} />
          <Field label="Office" value={loanOfficer.office_phone} />
          <Field label="Primary Phone" value={loanOfficer.phone} />
        </dl>
      </SectionCard>

      <SectionCard title="About" icon={<User size={20} />} delay={100}>
        {loanOfficer.about_me ? (
          <div
            className="pl-4 border-l-4 border-primary-300 leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-primary-600 [&_a:hover]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-semibold [&_em]:italic"
            dangerouslySetInnerHTML={{ __html: loanOfficer.about_me }}
          />
        ) : (
          <p className="text-slate-300 italic">No bio provided</p>
        )}
      </SectionCard>

      <SectionCard
        title="Application Links"
        icon={<Link2 size={20} />}
        delay={200}
      >
        <div className="space-y-3">
          <LinkRow label="Pre-application" href={loanOfficer.app_link} />
          <LinkRow
            label="Special Disclosure"
            href={loanOfficer.special_disclosure_link}
          />
        </div>
      </SectionCard>

      {socialLinks.length > 0 && (
        <SectionCard
          title="Social Profiles"
          icon={<Share2 size={20} />}
          delay={300}
        >
          <div className="grid grid-cols-3 gap-3">
            {socialLinks.map(link => (
              <SocialLink
                key={link.label}
                icon={link.icon}
                href={link.href!}
                label={link.label}
              />
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="Office Location"
        icon={<MapPin size={20} />}
        delay={socialLinks.length > 0 ? 400 : 300}
      >
        <dl className="grid grid-cols-2 gap-6">
          <Field label="Address" value={loanOfficer.office_street_addr} />
          <Field label="Branch NMLS" value={loanOfficer.branch_nmls} />
        </dl>
      </SectionCard>

      <If
        condition={!!loanOfficer?.google_place_id}
        then={
          <div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-fade-slide-in opacity-0"
            style={{
              animationDelay: `${socialLinks.length > 0 ? 500 : 400}ms`
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-primary-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </span>
              <h2 className="font-serif text-lg font-semibold text-slate-800">
                Google Reviews
              </h2>
            </div>
            <GoogleReviewsList
              placeId={loanOfficer?.google_place_id ?? ""}
              hideTitle
              reviews={reviews?.google}
            />
          </div>
        }
      />
    </div>
  )
}

export default LoanOfficerOverview
